import { Link, router } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import { common, pick } from "../../lib/i18n";
import type { BrandDetail, CurrentUser, ProductSummary } from "../../types";

const copy = {
  pl: { confirmDelete: (n: string) => `Usunąć markę „${n}" wraz ze wszystkimi logo?`, inactiveNote: "Marka nieaktywna — nie pojawia się w formularzu generacji", details: "Szczegóły", status: "Status", active: "Aktywna", inactive: "Nieaktywna", descriptionUpper: "OPIS", promptHintsUpper: "WSKAZÓWKI DO PROMPTÓW", manageLogos: "Zarządzaj logo", noLogo: "Brak logo.", addLogoLink: "Dodaj logo w edycji marki." },
  en: { confirmDelete: (n: string) => `Delete brand "${n}" with all its logos?`, inactiveNote: "Brand inactive — it does not appear in the generation form", details: "Details", status: "Status", active: "Active", inactive: "Inactive", descriptionUpper: "DESCRIPTION", promptHintsUpper: "PROMPT HINTS", manageLogos: "Manage logos", noLogo: "No logo.", addLogoLink: "Add a logo in brand editing." },
};

type Props = {
  brand: BrandDetail;
  current_user: CurrentUser;
  products?: ProductSummary[];
};

export default function BrandsShow({ brand, current_user, products = [] }: Props) {
  const initials = current_user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const canEdit  = current_user.role === "admin" || current_user.role === "moderator";
  const t = pick(copy);
  const c = common();

  const handleDestroy = () => {
    if (confirm(t.confirmDelete(brand.name))) {
      router.delete(`/brands/${brand.id}`);
    }
  };

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 720 }}>
        <div className="page-hd">
          <div>
            <h2>{brand.name}</h2>
            {!brand.active && (
              <p style={{ color: "var(--muted)" }}>{t.inactiveNote}</p>
            )}
          </div>
          {canEdit && (
            <div className="right">
              <Link href={`/brands/${brand.id}/edit`} className="btn btn-secondary btn-sm">{c.actions.edit}</Link>
              <button className="btn btn-ghost btn-sm" onClick={handleDestroy}>{c.actions.delete}</button>
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div className="panel" style={{ gridColumn: brand.logo_urls.length > 0 ? "1" : "1 / -1" }}>
            <div className="panel-hd"><h3>{t.details}</h3></div>
            <div className="panel-body">
              <div className="field-row">
                <span>Slug</span>
                <span>{brand.slug}</span>
              </div>
              <div className="field-row">
                <span>{t.status}</span>
                <span>{brand.active ? t.active : t.inactive}</span>
              </div>
              {brand.description && (
                <div style={{ marginTop: 10 }}>
                  <div className="mono" style={{ fontSize: 10.5, color: "var(--muted-2)", marginBottom: 4 }}>{t.descriptionUpper}</div>
                  <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{brand.description}</p>
                </div>
              )}
              {brand.prompt_hints && (
                <div style={{ marginTop: 10 }}>
                  <div className="mono" style={{ fontSize: 10.5, color: "var(--muted-2)", marginBottom: 4 }}>{t.promptHintsUpper}</div>
                  <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap", color: "var(--muted)" }}>
                    {brand.prompt_hints}
                  </p>
                </div>
              )}
            </div>
          </div>

          {brand.logo_urls.length > 0 && (
            <div className="panel">
              <div className="panel-hd">
                <h3>Logo</h3>
                <span className="mono sub">{brand.logo_urls.length} / 5</span>
              </div>
              <div className="panel-body">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {brand.logo_urls.map((logo) => (
                    <div
                      key={logo.id}
                      style={{
                        background: "var(--bg-2)",
                        border: "1px solid var(--line)",
                        borderRadius: 6,
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 100,
                        height: 80,
                      }}
                    >
                      <img
                        src={logo.url}
                        alt="logo"
                        style={{ maxHeight: 56, maxWidth: 80, objectFit: "contain" }}
                      />
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <Link
                    href={`/brands/${brand.id}/edit`}
                    className="btn btn-ghost btn-sm"
                    style={{ marginTop: 12 }}
                  >
                    {t.manageLogos}
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>

        {brand.logo_urls.length === 0 && canEdit && (
          <div style={{ marginTop: 14, padding: "12px 14px", background: "var(--bg-2)", border: "1px dashed var(--line)", borderRadius: 6, fontSize: 12.5, color: "var(--muted)" }}>
            {t.noLogo} <Link href={`/brands/${brand.id}/edit`} style={{ color: "var(--ink)" }}>{t.addLogoLink}</Link>
          </div>
        )}
      </div>
    </AppShell>
  );
}
